import React, { useEffect, useCallback } from "react";
import EditMenu from "../EditMenu";
import CategoryToolbar from "./components/CategoryToolbar";
import CategoryTable from "./components/CategoryTable";
import DeleteCategoryModal from "./components/DeleteCategoryModal";
import LoadingScreen from "../../../components/LoadingScreen";
import CategoryModal from "./components/category-modal/CategoryModal";
import { useCategories } from "./hooks/useCategories";
import { useCategoryActions } from "./hooks/useCategoryActions";
import { useCategoryPageState } from "./hooks/useCategoryPageState";
import { PageTitle } from "src/shared/PageTitle";
import { useUser } from "src/shared/context/UserContext";
import { pageTitle } from "src/shared/utils/brand";
import { useUserTheme } from "src/shared/utils/useUserTheme";

interface CategoriesPageProps {
  showEditMenu?: boolean;
}

const CategoriesPage: React.FC<CategoriesPageProps> = ({ showEditMenu = true }) => {
  const { user } = useUser();

  const { categories, loading: fetchLoading, totalRecords, fetchCategories, fetchCategoriesQuietly, reorderCategoriesLocally } = useCategories();
  const { loading: actionLoading, createCategory, updateCategory, deleteCategory, reorderCategory } = useCategoryActions();

  const {
    showDeletedCategories,
    setShowDeletedCategories,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    searchTerm,
    setSearchTerm,
    filteredTerm,
    isAddModalOpen,
    setIsAddModalOpen,
    isEditModalOpen,
    setIsEditModalOpen,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    selectedCategory,
    setSelectedCategory,
  } = useCategoryPageState();

  const loading = fetchLoading || actionLoading;

  const businessType = user?.businessType ?? "";
  const { sidebarThemeClass } = useUserTheme();

  useEffect(() => {
    document.title = pageTitle("Categories › Settings");
  }, []);

  useEffect(() => {
    if (searchTerm) setPage(0);
  }, [searchTerm, setPage]);

  // Fetch categories whenever page, rowsPerPage, searchTerm, or showDeleted changes
  useEffect(() => {
    fetchCategories(showDeletedCategories, page, rowsPerPage, filteredTerm);
  }, [showDeletedCategories, page, rowsPerPage, filteredTerm, fetchCategories]);

  const handleAddCategory = useCallback(
    async (categoryName: string, type: string, displayOrder?: string) => {
      const response = await createCategory({
        categoryName: categoryName.toUpperCase(),
        type,
      });

      if (response) {
        setIsAddModalOpen(false);

        fetchCategories(showDeletedCategories, page, rowsPerPage, filteredTerm);
      }
    },
    [createCategory, fetchCategories, showDeletedCategories, page, rowsPerPage, filteredTerm, setIsAddModalOpen],
  );

  const handleEditCategory = useCallback(
    async (categoryName: string, type: string, displayOrder?: string) => {
      await updateCategory(selectedCategory.id, { categoryName: categoryName.toUpperCase(), ...(businessType === "Saloon" && { type: type }) });

      if (displayOrder && Number(displayOrder) > 0) {
        await reorderCategory(selectedCategory.id, Number(displayOrder), false);
      }

      setIsEditModalOpen(false);
      fetchCategories(showDeletedCategories, page, rowsPerPage, filteredTerm);
    },
    [updateCategory, reorderCategory, selectedCategory.id, businessType, fetchCategories, showDeletedCategories, page, rowsPerPage, filteredTerm, setIsEditModalOpen],
  );

  const handleDeleteCategory = useCallback(async () => {
    await deleteCategory(selectedCategory.id);
    setIsDeleteModalOpen(false);
    fetchCategories(showDeletedCategories, page, rowsPerPage, filteredTerm);
  }, [deleteCategory, selectedCategory.id, fetchCategories, showDeletedCategories, page, rowsPerPage, filteredTerm, setIsDeleteModalOpen]);

  const handleEditClick = (id: string, name: string) => {
    if (!showDeletedCategories) {
      const category = categories.find((cat) => cat._id.$oid === id);
      const categoryIndex = categories.findIndex((cat) => cat._id.$oid === id);
      const currentDisplayOrder = category?.displayOrder ?? page * rowsPerPage + categoryIndex + 1;
      setSelectedCategory({ id, name, type: category?.type || "", displayOrder: currentDisplayOrder });
      setIsEditModalOpen(true);
    }
  };

  const handleDeleteClick = (id: string, name: string) => {
    if (!showDeletedCategories) {
      setSelectedCategory({ id, name, type: "" });
      setIsDeleteModalOpen(true);
    }
  };

  const handleReorderCategory = useCallback(
    async (categoryId: string, sourceIndex: number, targetIndex: number) => {
      const newIndex = page * rowsPerPage + targetIndex + 1;
      reorderCategoriesLocally(sourceIndex, targetIndex, page, rowsPerPage);
      const result = await reorderCategory(categoryId, newIndex);
      if (result) {
        fetchCategoriesQuietly(showDeletedCategories, page, rowsPerPage, filteredTerm);
      } else {
        // Revert to server data on failure
        fetchCategories(showDeletedCategories, page, rowsPerPage, filteredTerm);
      }
    },
    [page, rowsPerPage, reorderCategoriesLocally, reorderCategory, fetchCategoriesQuietly, showDeletedCategories, filteredTerm, fetchCategories],
  );

  const menu = { Category: "Category" };

  return (
    <div className="p-1">
      <div className={`flex flex-col w-full ${showEditMenu ? "h-[91vh]" : "h-auto"} shadow-inner rounded-lg items-center ${sidebarThemeClass} overflow-hidden`}>
        {showEditMenu && <PageTitle title="- Settings › Categories" />}
        {showEditMenu && <EditMenu editmenu={menu} />}

        <div className="p-2 w-full">
          <CategoryToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            showDeleted={showDeletedCategories}
            onShowDeletedChange={setShowDeletedCategories}
            onAddCategory={() => setIsAddModalOpen(true)}
          />
        </div>

        <div className="w-full flex justify-center p-2 flex-1 overflow-auto">
          <CategoryTable
            categories={categories}
            page={page}
            rowsPerPage={rowsPerPage}
            totalRecords={totalRecords}
            showDeleted={showDeletedCategories}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onReorderCategory={handleReorderCategory}
          />
        </div>

        <CategoryModal open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSubmit={handleAddCategory} businessType={businessType} />

        <CategoryModal
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleEditCategory}
          initialName={selectedCategory.name}
          initialType={selectedCategory.type}
          initialDisplayOrder={selectedCategory.displayOrder}
          isEdit
          businessType={businessType}
        />

        <DeleteCategoryModal open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} onConfirm={handleDeleteCategory} categoryName={selectedCategory.name} />

        {loading && <LoadingScreen />}
      </div>
    </div>
  );
};

export default CategoriesPage;
